import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, RefreshCw, X } from 'lucide-react';
import {
  useQueryStates,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
} from 'nuqs';
import {
  Avatar,
  Badge,
  Button,
  Combobox,
  DateRangePicker,
  Drawer,
  Select,
  SelectItem,
  Spinner,
  Table,
  type BadgeProps,
} from '@ntconsult/aluma-react';
import { EmptyState } from '@common/components/EmptyState';
import { useIsMobile } from '@common/hooks/useMediaQuery';
import { useDebouncedValue } from '@common/hooks/useDebouncedValue';
import { formatDateTime } from '@common/utils/format';
import { useUsers } from '@features/users/hooks/useUsers';
import { useAuditLogs } from '../hooks/useAuditLogs';
import {
  AUDIT_ACTIONS,
  AUDIT_RESOURCE_TYPES,
  type AuditAction,
  type AuditLog as AuditLogType,
  type AuditResourceType,
} from '../types';

const actionTones: Record<AuditAction, BadgeProps['tone']> = {
  // Auth
  'auth.login': 'success',
  'auth.logout': 'neutral',
  'auth.login_failure': 'warning',
  'auth.login_locked': 'danger',
  'auth.refresh_reuse': 'danger',
  'auth.csrf_failure': 'danger',
  // Users
  'user.create': 'info',
  'user.update': 'warning',
  'user.delete': 'danger',
  // Folders
  'folder.create': 'info',
  'folder.update': 'warning',
  'folder.delete': 'danger',
  'folder.view': 'neutral',
  'folder.download': 'neutral',
  'folder.owner_change': 'warning',
  // Files
  'file.upload': 'info',
  'file.migrated': 'info',
  'file.view': 'neutral',
  'file.download': 'neutral',
  'file.bulk_download': 'neutral',
  'file.update': 'warning',
  'file.delete': 'danger',
  'file.owner_change': 'warning',
  // Shares
  'share.grant_user': 'info',
  'share.grant_group': 'info',
  'share.grant_org': 'info',
  'share.grant_link': 'info',
  'share.access': 'neutral',
  'share.download': 'neutral',
  'share.update': 'warning',
  'share.revoke': 'danger',
};

const auditFilterParsers = {
  action: parseAsStringLiteral(AUDIT_ACTIONS),
  resource: parseAsStringLiteral(AUDIT_RESOURCE_TYPES),
  userId: parseAsString,
  startDate: parseAsString,
  endDate: parseAsString,
  page: parseAsInteger.withDefault(1),
};

const PAGE_SIZE = 20;

export function AuditLog() {
  const { t } = useTranslation(['audit', 'common']);
  const [filters, setFilters] = useQueryStates(auditFilterParsers, {
    history: 'replace',
  });
  const { action, resource, userId, startDate, endDate, page } = filters;

  const isMobile = useIsMobile();
  const [selectedLog, setSelectedLog] = useState<AuditLogType | null>(null);

  const { data, isLoading, refetch, isFetching } = useAuditLogs({
    action: action ?? undefined,
    resourceType: resource ?? undefined,
    userId: userId ?? undefined,
    startDate: startDate ?? undefined,
    endDate: endDate ?? undefined,
    page,
    limit: PAGE_SIZE,
  });

  const logs = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const totalPages = data?.meta?.totalPages ?? 1;

  const dateRange =
    startDate && endDate
      ? { from: new Date(startDate), to: new Date(endDate) }
      : undefined;

  const groupedActions: Array<{ category: string; actions: AuditAction[] }> = [
    {
      category: 'auth',
      actions: AUDIT_ACTIONS.filter((a) => a.startsWith('auth.')),
    },
    {
      category: 'user',
      actions: AUDIT_ACTIONS.filter((a) => a.startsWith('user.')),
    },
    {
      category: 'folder',
      actions: AUDIT_ACTIONS.filter((a) => a.startsWith('folder.')),
    },
    {
      category: 'file',
      actions: AUDIT_ACTIONS.filter((a) => a.startsWith('file.')),
    },
    {
      category: 'share',
      actions: AUDIT_ACTIONS.filter((a) => a.startsWith('share.')),
    },
  ];

  const clearFilters = () => {
    void setFilters({
      action: null,
      resource: null,
      userId: null,
      startDate: null,
      endDate: null,
      page: 1,
    });
  };

  const hasAnyFilter = !!(
    action ||
    resource ||
    userId ||
    startDate ||
    endDate
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">
            {t('audit:title')}
          </h1>
          {!isLoading && (
            <p className="text-sm text-foreground-muted mt-1">
              {t('audit:resultsCount', { count: total })}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {hasAnyFilter && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              leftIcon={<X className="w-4 h-4" />}
            >
              {t('audit:filters.clear')}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => refetch()}
            loading={isFetching}
            leftIcon={<RefreshCw className="w-4 h-4" />}
            aria-label={t('common:actions.refresh')}
          >
            <span className="hidden sm:inline">{t('common:actions.refresh')}</span>
          </Button>
        </div>
      </div>

      {/* Filters — grid de colunas iguais no desktop: uma única linha, sem
          sobrepor. Cada coluna é minmax(0,1fr) (grid-cols-*), então encolhe
          junto. Os triggers de Select/Combobox trazem min-w-[200/220px]
          embutido no botão interno (fora do alcance do className do wrapper);
          neutralizamos com [&>button]:min-w-0! para deixá-los encolher.
          No mobile vira grid-cols-1 e empilha. */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-surface rounded-lg border border-border">
        <Filter
          className="hidden sm:block w-4 h-4 text-foreground-subtle shrink-0"
          aria-hidden="true"
        />

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-3 flex-1 min-w-0">
          <Select
            value={action ?? ''}
            onValueChange={(value) =>
              void setFilters({
                action: (value || null) as AuditAction | null,
                page: 1,
              })
            }
            className="w-full min-w-0 [&>button]:min-w-0!"
            aria-label={t('audit:table.action')}
          >
            <SelectItem value="">{t('audit:filters.allActions')}</SelectItem>
            {groupedActions.map((group) =>
              group.actions.map((a) => (
                <SelectItem key={a} value={a}>
                  {t(`audit:actions.${a}`, a)}
                </SelectItem>
              )),
            )}
          </Select>

          <Select
            value={resource ?? ''}
            onValueChange={(value) =>
              void setFilters({
                resource: (value || null) as AuditResourceType | null,
                page: 1,
              })
            }
            className="w-full min-w-0 [&>button]:min-w-0!"
            aria-label={t('audit:table.resource')}
          >
            <SelectItem value="">{t('audit:filters.allResources')}</SelectItem>
            {AUDIT_RESOURCE_TYPES.map((r) => (
              <SelectItem key={r} value={r}>
                {t(`audit:categories.${r}`)}
              </SelectItem>
            ))}
          </Select>

          <UserFilter
            value={userId ?? ''}
            onChange={(id) =>
              void setFilters({ userId: id || null, page: 1 })
            }
            className="w-full min-w-0 [&>button]:min-w-0!"
          />

          <DateRangePicker
            value={dateRange}
            onChange={(range) => {
              void setFilters({
                startDate: range?.from ? range.from.toISOString() : null,
                endDate: range?.to ? range.to.toISOString() : null,
                page: 1,
              });
            }}
            placeholder={t('audit:filters.datePlaceholder')}
            clearable
            showClearButton
            className="w-full min-w-0"
            aria-label={t('audit:filters.dateRange')}
          />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : logs.length === 0 ? (
        <EmptyState
          title={t('audit:noRecords')}
          description={t('audit:noRecordsMessage')}
        />
      ) : (
        <>
          {isMobile ? (
            <ul
              role="list"
              className="bg-surface rounded-lg border border-border divide-y divide-border overflow-hidden"
            >
              {logs.map((log) => (
                <li key={log.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedLog(log)}
                    className="flex w-full items-start gap-3 px-3 py-3 text-left cursor-pointer hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2"
                    aria-label={t('audit:openDetails')}
                  >
                    <Avatar name={log.user?.name || 'U'} size="sm" />
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-center justify-between gap-2 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {log.user?.name ?? t('audit:anonymous')}
                        </p>
                        <span className="text-xs text-foreground-muted shrink-0">
                          {formatDateTime(log.createdAt)}
                        </span>
                      </div>
                      {log.user?.email && (
                        <p className="text-xs text-foreground-muted truncate">
                          {log.user.email}
                        </p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="soft" tone={actionTones[log.action] ?? 'neutral'}>
                          {t(`audit:actions.${log.action}`, log.action)}
                        </Badge>
                        {log.resourceType && (
                          <span className="text-xs text-foreground-muted">
                            {t(`audit:categories.${log.resourceType}`, log.resourceType)}
                          </span>
                        )}
                      </div>
                      {log.resourceName && (
                        <p className="text-xs text-foreground-muted truncate">
                          {log.resourceName}
                        </p>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <Table containerClassName="bg-surface rounded-lg border border-border">
              <Table.Head>
                <Table.Row>
                  <Table.Header>{t('audit:table.user')}</Table.Header>
                  <Table.Header className="w-44">
                    {t('audit:table.action')}
                  </Table.Header>
                  <Table.Header className="w-28">
                    {t('audit:table.resource')}
                  </Table.Header>
                  <Table.Header>{t('audit:table.details')}</Table.Header>
                  <Table.Header className="w-44">
                    {t('audit:table.dateTime')}
                  </Table.Header>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {logs.map((log) => (
                  <Table.Row
                    key={log.id}
                    onClick={() => setSelectedLog(log)}
                    className="cursor-pointer hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2"
                  >
                    <Table.Cell>
                      <div className="flex items-center gap-3">
                        <Avatar name={log.user?.name || 'U'} size="sm" />
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {log.user?.name ?? t('audit:anonymous')}
                          </p>
                          {log.user?.email && (
                            <p className="text-xs text-foreground-muted">
                              {log.user.email}
                            </p>
                          )}
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant="soft" tone={actionTones[log.action] ?? 'neutral'}>
                        {t(`audit:actions.${log.action}`, log.action)}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-foreground-muted">
                        {log.resourceType
                          ? t(`audit:categories.${log.resourceType}`, log.resourceType)
                          : '—'}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-foreground-muted text-sm">
                        {log.resourceName || '—'}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="text-foreground-muted">
                      {formatDateTime(log.createdAt)}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-foreground-muted">
                {t('audit:rangeLabel', {
                  from: (page - 1) * PAGE_SIZE + 1,
                  to: Math.min(page * PAGE_SIZE, total),
                  total,
                })}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    void setFilters({ page: Math.max(1, page - 1) })
                  }
                  disabled={page === 1}
                >
                  {t('common:pagination.previous')}
                </Button>
                <span className="text-sm text-foreground-muted">
                  {t('common:pagination.page')} {page}{' '}
                  {t('common:pagination.of')} {totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    void setFilters({ page: Math.min(totalPages, page + 1) })
                  }
                  disabled={page === totalPages}
                >
                  {t('common:pagination.next')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <AuditLogDetailsDrawer
        log={selectedLog}
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </div>
  );
}

// ─── User filter (Combobox com busca debounced) ─────────────────────────────

interface UserFilterProps {
  value: string;
  onChange: (userId: string) => void;
  className?: string;
}

function UserFilter({ value, onChange, className }: UserFilterProps) {
  const { t } = useTranslation(['audit']);
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search, 300);

  const { data } = useUsers({ search: debounced });
  const users = data?.data ?? [];
  const selected = users.find((u) => u.id === value);

  return (
    <Combobox
      value={value}
      onValueChange={(v) => onChange(v ?? '')}
      placeholder={t('audit:filters.userPlaceholder')}
      filter={() => true}
      className={className}
    >
      <Combobox.Trigger
        className="w-full"
        renderValue={() =>
          selected ? (
            <span className="inline-flex items-center gap-2 min-w-0">
              <Avatar name={selected.name} size="xs" />
              <span className="truncate">{selected.name}</span>
            </span>
          ) : (
            t('audit:filters.userPlaceholder')
          )
        }
      />
      <Combobox.Content withSearchInput={false}>
        <Combobox.Input
          placeholder={t('audit:filters.userSearchPlaceholder')}
          onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
        />
        <Combobox.List>
          <Combobox.Empty>{t('audit:filters.noUsersFound')}</Combobox.Empty>
          {users.map((user) => (
            <Combobox.Option
              key={user.id}
              value={user.id}
              keywords={[user.name, user.email]}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Avatar name={user.name} size="sm" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {user.name}
                  </p>
                  <p className="text-xs text-foreground-muted truncate">
                    {user.email}
                  </p>
                </div>
              </div>
            </Combobox.Option>
          ))}
        </Combobox.List>
      </Combobox.Content>
    </Combobox>
  );
}

// ─── Drawer de detalhes ──────────────────────────────────────────────────────

interface AuditLogDetailsDrawerProps {
  log: AuditLogType | null;
  open: boolean;
  onClose: () => void;
}

function AuditLogDetailsDrawer({ log, open, onClose }: AuditLogDetailsDrawerProps) {
  const { t } = useTranslation(['audit']);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="right"
      size="md"
      ariaLabel={t('audit:details.title')}
    >
      <Drawer.Header
        title={t('audit:details.title')}
        subtitle={log ? t(`audit:actions.${log.action}`, log.action) : ''}
        onClose={onClose}
      />
      <Drawer.Body>
        {log && (
          <div className="space-y-5">
            <DetailRow
              label={t('audit:details.user')}
              value={
                log.user ? (
                  <span className="inline-flex items-center gap-2">
                    <Avatar name={log.user.name} size="xs" />
                    <span>
                      {log.user.name}{' '}
                      <span className="text-foreground-muted">
                        ({log.user.email})
                      </span>
                    </span>
                  </span>
                ) : (
                  t('audit:anonymous')
                )
              }
            />
            <DetailRow
              label={t('audit:table.action')}
              value={
                <Badge variant="soft" tone={actionTones[log.action] ?? 'neutral'}>
                  {t(`audit:actions.${log.action}`, log.action)}
                </Badge>
              }
            />
            <DetailRow
              label={t('audit:table.resource')}
              value={
                log.resourceType
                  ? t(`audit:categories.${log.resourceType}`, log.resourceType)
                  : '—'
              }
            />
            <DetailRow
              label={t('audit:details.resourceName')}
              value={log.resourceName || '—'}
            />
            <DetailRow
              label={t('audit:details.resourceId')}
              value={
                log.resourceId ? (
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                    {log.resourceId}
                  </code>
                ) : (
                  '—'
                )
              }
            />
            <DetailRow
              label={t('audit:table.dateTime')}
              value={formatDateTime(log.createdAt)}
            />
            <DetailRow
              label={t('audit:details.ipAddress')}
              value={
                log.ipAddress ? (
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                    {log.ipAddress}
                  </code>
                ) : (
                  '—'
                )
              }
            />
            <DetailRow
              label={t('audit:details.userAgent')}
              value={
                log.userAgent ? (
                  <span className="text-xs text-foreground-muted break-all">
                    {log.userAgent}
                  </span>
                ) : (
                  '—'
                )
              }
            />
            {log.metadata && Object.keys(log.metadata).length > 0 && (
              <div>
                <p className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-1.5">
                  {t('audit:details.metadata')}
                </p>
                <pre className="text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
                  {JSON.stringify(log.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Drawer.Body>
    </Drawer>
  );
}

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-3 text-sm">
      <span className="text-xs font-medium text-foreground-muted uppercase tracking-wide pt-0.5">
        {label}
      </span>
      <div className="text-foreground min-w-0">{value}</div>
    </div>
  );
}
